import { Box, Skeleton, Typography } from '@mui/material'
import React, { useState } from 'react'
import { Icons } from '../../../utils/utilities'

/**
 * CustomImage component for displaying images with loading and error states.
 * @param {Object} props - The props object.
 * @param {string} props.src - The source URL of the image.
 * @param {'fill'|'contain'|'cover'|'none'|'scale-down'} [props.fit='cover'] - The object-fit value for image.
 * @param {'rounded' | 'circular' } [props.variant] - The alt text for the image.
 * @param {string} [props.alt] - The alt text for the image.
 * @param {string} [props.width='100%'] - The width of the image container.
 * @param {string} [props.height='200px'] - The height of the image container.
 * @param {string} [props.aspectRatio='auto'] - Aspect ratio.
 * @param {JSX.Element} [props.errorFallback] - Custom error fallback JSX.Element.
 * @param {import('@mui/material').SxProps} [props.containerSx] - Additional styles for the container Box.
 * @param {import('@mui/material').SxProps} [props.imageSx] - Additional styles for the image element.
 * @param {Object} props... - Other props passed to the img element.
 * @returns {JSX.Element} The CustomImage component.
 */
const CustomImage = ( { loadingFallback, errorFallback, variant = "rounded", children, src, alt, fit = 'cover', width = '100%', height = '100%', containerSx, imageSx, aspectRatio = "initial", ...props } ) => {
    const [loading, setLoading] = useState( true )
    const [error, setError] = useState( false )

    if ( !src )
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: "center", alignItems: 'center', gap: 1, p: 2, textAlign: 'center', color: "text.secondary", fontSize: "20px", height, aspectRatio, width, bgcolor: theme => theme.palette.contentBg, border: theme => theme.border[1], borderRadius: theme => theme.layout.borderRadius }}>
                {Icons.default.BrokenImage}
                {parseInt( width ) > 150 && <Typography variant="body2" color="textSecondary" sx={{ fontSize: "10px" }}>
                    Invalid image URL
                </Typography>}
            </Box> )

    return (
        <Box
            sx={{
                position: 'relative',
                width: width,
                height: height,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: variant === "circular" ? "50%" : '4px',
                overflow: 'clip',
                aspectRatio,
                ...( containerSx || {} )
            }}
        >
            {/* 1. Loading State Placeholder */}
            {loading && !error && (
                loadingFallback ? loadingFallback : <Skeleton variant="rectangular" sx={{ position: "absolute", inset: 0, width, height, aspectRatio }} />
            )}

            {/* 2. Error State Placeholder */}
            {error && ( errorFallback ?? (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: "center", alignItems: 'center', gap: 1, p: 2, textAlign: 'center', color: "text.secondary", fontSize: "20px", height: "100%", width: "100%", bgcolor: theme => theme.palette.contentBg, border: theme => theme.border[1], borderRadius: theme => theme.layout.borderRadius }}>
                    {Icons.default.BrokenImage}
                    {parseInt( width ) > 150 && <Typography variant="body2" color="textSecondary" sx={{ fontSize: "10px" }}>
                        Invalid image URL
                    </Typography>}
                </Box>
            ) )}

            {/* 3. Actual Image Element */}
            {!error && (
                <Box
                    component="img"
                    src={src}
                    alt={alt}
                    onLoad={() => setLoading( false )}
                    onError={() => {
                        setError( true )
                        setLoading( false )
                    }}
                    {...props}
                    sx={[{
                        width: "30px",
                        height: "30px",
                        width,
                        height,
                        objectFit: fit,
                        visibility: loading ? 'none' : 'block',
                        aspectRatio,
                        ...( imageSx || {} )
                    }, ...( Array.isArray( props.sx ) ? props.sx : [props.sx] )]} />
            )}
            {children}
        </Box>
    )
}

export default CustomImage